// ── DEV-only canary for layout reads in the rAF update phase ─────────────
// Runtime enforcement of the invariant: during update(), the render loop
// is write-only. Any getBoundingClientRect() call inside update() — other
// than the one SceneViewport makes for its own cache — is a regression
// that will reintroduce layout thrashing. Warn loudly in dev so mistakes
// surface at the point of authorship instead of weeks later on-device.
//
// Stripped from production builds by Vite's import.meta.env.DEV dead-code
// elimination; the installLayoutReadGuard() call in main.ts is the only
// entry point.

import { isMeasuringSceneRect } from '../layout/sceneViewport';

let insideUpdate = false;
let warnCount = 0;
const MAX_WARNINGS = 20;

export function markUpdateStart(): void {
  insideUpdate = true;
}

export function markUpdateEnd(): void {
  insideUpdate = false;
}

export function installLayoutReadGuard(): void {
  // `import.meta.env.DEV` is injected by Vite at build time; cast through
  // unknown because the vite/client ambient types aren't wired in.
  const meta = import.meta as unknown as { env?: { DEV?: boolean } };
  if (!meta.env?.DEV) return;
  const origGbcr = Element.prototype.getBoundingClientRect;
  Element.prototype.getBoundingClientRect = function patched(this: Element) {
    if (insideUpdate && !isMeasuringSceneRect() && warnCount < MAX_WARNINGS) {
      warnCount += 1;
      // eslint-disable-next-line no-console
      console.warn(
        '[Loop OS] getBoundingClientRect() called during rAF update phase.',
        'Render loop must be write-only. Read via SceneViewport or cached state.',
        this,
      );
      // eslint-disable-next-line no-console
      console.trace('[Loop OS] layout-read stack');
      if (warnCount === MAX_WARNINGS) {
        // eslint-disable-next-line no-console
        console.warn('[Loop OS] further layout-read warnings suppressed.');
      }
    }
    return origGbcr.call(this);
  };
}
